import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import { Box, CircularProgress, Theme, useMediaQuery } from '@mui/material';
import { useRef, useState } from 'react';

import { useCreatePieceDocumentMutation, useDeletePiecePageMutation } from '@api/piece';
import { Piece, PieceDoc } from '@apiContract';
import { CameraCapture } from '@components/CameraCapture';
import { CaptureActions } from '@components/CaptureActions';
import { CaptureHeader } from '@components/CaptureHeader';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';
import { DoubleButton } from '@components/DoubleButton';
import { ICropperRef } from '@components/ImageCropper';
import { ImageEditor } from '@components/ImageEditor';
import { SingleButton } from '@components/SingleButton';
import { useScreenOrientation } from '@hooks/useScreenOrientation';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { resizeImage, rotateImage } from '@utils/image';

import { CaptureThumbnails } from './components/CaptureThumbnails';
import { getInitialThumbnails } from './helpers/thumbnails';
import { usePiecePageInitiation } from './hooks/usePiecePageInitiation';
import { useUploadImageS3 } from './hooks/useUploadImageS3';
import { CaptureMode, IThumbnail, IThumbnailId } from './types';

interface Mode {
  title: string;
  value: CaptureMode;
}

const modes: Mode[] = [
  {
    title: 'Scan',
    value: 'scanning',
  },
  {
    title: 'Retake',
    value: 'retaking',
  },
  {
    title: 'Edit',
    value: 'editing',
  },
  {
    title: 'Crop',
    value: 'cropping',
  },
];

const getModeByValue = (value: CaptureMode) => modes.find((mode) => mode.value === value) as Mode;

interface OpenScanActionContainerProps {
  currentPiece: Piece;
  onFinish?: () => void;
  onClose: () => void;
}

export const DialogMailpieceOpenAndScan = ({ currentPiece, onFinish, onClose }: OpenScanActionContainerProps) => {
  const [createPieceDocument] = useCreatePieceDocumentMutation();
  const [deletePiecePage, { isLoading: isDeleteLoading }] = useDeletePiecePageMutation();

  const { getImageFromS3 } = useUploadImageS3();
  const { runPiecePageInitiation } = usePiecePageInitiation();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const orientation = useScreenOrientation();

  const [mode, setMode] = useState<Mode>(() => getModeByValue('scanning'));
  const [previewLoading, setPreviewLoading] = useState(false);
  const [originalImage, setOriginalImage] = useState('');
  const [selectedThumbnailId, setSelectedThumbnailId] = useState<IThumbnailId | null>(null);
  const [thumbnails, setThumbnails] = useState<IThumbnail[]>(() => getInitialThumbnails(currentPiece));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bufferImages, setBufferImages] = useState({
    original: '',
    cropped: '',
  });
  const [croppedImage, setCroppedImage] = useState('');
  const [cropMode, setCropMode] = useState(false);

  const [piecePageInitOperations, setPiecePageInitOperations] = useState<IThumbnailId[]>([]);
  const [discardThumbnailDialog, setDiscardThumbnailDialog] = useState<IThumbnailId | null>(null);

  const [sessionPieceDoc, setSessionPieceDoc] = useState<PieceDoc | null>(currentPiece.pieceDoc || null);

  const editThumbnail = (thumbnail: IThumbnail) =>
    setThumbnails((thumbnailsList) => thumbnailsList.map((item) => (item.id === thumbnail.id ? thumbnail : item)));

  const clearAllTempThumbnails = () =>
    setThumbnails((thumbnailsList) => thumbnailsList.filter((thumbnail) => typeof thumbnail.id === 'number'));

  const clearBufferImage = () =>
    setBufferImages({
      original: '',
      cropped: '',
    });

  const selectThumnbail = async (thumbnailId: IThumbnailId) => {
    const clickedThumbnail = thumbnails.find((thumbnail) => thumbnail.id === thumbnailId);
    if (!clickedThumbnail) {
      toast.error('Thumbnail was not found', { duration: 3000 });
      return;
    }

    try {
      setCropMode(false);
      setSelectedThumbnailId(clickedThumbnail.id);
      setMode(getModeByValue('editing'));
      let original: string = '',
        cropped: string = '';
      if (clickedThumbnail.image && !clickedThumbnail.image.split(',')[1]) {
        setPreviewLoading(true);
        original = await getImageFromS3(clickedThumbnail.image);
      }
      if (clickedThumbnail.croppedImage && !clickedThumbnail.croppedImage.split(',')[1]) {
        setPreviewLoading(true);
        cropped = await getImageFromS3(clickedThumbnail.croppedImage);
      }
      editThumbnail({
        id: clickedThumbnail.id,
        image: original || clickedThumbnail.image,
        croppedImage: cropped || clickedThumbnail.croppedImage,
        order: clickedThumbnail.order,
        thumbnailImage: clickedThumbnail.thumbnailImage,
      });
      setOriginalImage(original || clickedThumbnail.image);
      setCroppedImage(cropped || clickedThumbnail.croppedImage);
      clearBufferImage();
    } catch (error) {
      console.error(error);
      toast.error('server error', { duration: 3000 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseDiscardThumbnailDialog = () => setDiscardThumbnailDialog(null);
  const handleConfirmDiscardThumbnailDialog = () => {
    if (!discardThumbnailDialog) return;

    handleCloseDiscardThumbnailDialog();
    selectThumnbail(discardThumbnailDialog);
    clearAllTempThumbnails();
  };

  const onPiecePageInitOperationComplete = (thumbnail: IThumbnail, operationId: string | number) => {
    setPiecePageInitOperations((operations) => operations.filter((id) => id !== operationId));

    setThumbnails((thumbnailsList) => {
      const existingThumbnail = thumbnailsList.find((thumbnailItem) => thumbnailItem.id === thumbnail.id);

      return existingThumbnail
        ? thumbnails.map((thumbnailItem) => (thumbnailItem.id === thumbnail.id ? thumbnail : thumbnailItem))
        : [...thumbnails.filter((thumbnailFilter) => thumbnailFilter.id !== operationId), thumbnail];
    });
  };

  const onPiecePageInitOperationError = (operationId: string | number) => {
    toast.error('Piece page creation error', { duration: 3000 });
    setPiecePageInitOperations((operations) => operations.filter((id) => id !== operationId));
  };
  const saveBufferImage = () => {
    if (!bufferImages.cropped && !bufferImages.original) {
      setBufferImages({ original: originalImage, cropped: croppedImage });
    }
    setOriginalImage('');
    setCroppedImage('');
  };
  const applyBufferImage = () => {
    setBufferImages((images) => {
      setOriginalImage(images.original);
      setCroppedImage(images.cropped);
      return { original: '', cropped: '' };
    });
  };

  const cropperRef = useRef<ICropperRef | null>(null);

  const turnOffCropMode = () => setCropMode(false);

  const handleDeleteOpen = () => setDeleteOpen(true);
  const handleDeleteClose = () => setDeleteOpen(false);

  const resetToScan = () => {
    setMode(getModeByValue('scanning'));
    clearBufferImage();
    setOriginalImage('');
    setCroppedImage('');
    setSelectedThumbnailId(null);
  };

  const handleCancelEditing = () => {
    clearAllTempThumbnails();
    resetToScan();
  };

  const handleCaptureRetaking = async (imageSrc: string) => {
    setOriginalImage(imageSrc);
    setMode(getModeByValue('editing'));
  };

  const handleFinishScanning = () => {
    const fn = onFinish ? onFinish : onClose;
    fn();
  };

  const getNewOrder = () => {
    if (!thumbnails.length) return 1;

    const highestOrder = Math.max(...thumbnails.map((thumbnail) => thumbnail.order));
    return highestOrder + 1;
  };

  const deleteThumbnail = (id: string | number) =>
    setThumbnails((thumbnailsList) => thumbnailsList.filter((item) => item.id !== id));

  const handleRotateImage = () => {
    rotateImage(croppedImage || originalImage, -90, (result) => {
      const fn = result ? setCroppedImage : setOriginalImage;
      fn(result);
    });
  };

  const toggleCropMode = () => {
    setCropMode((modeCrop) => {
      const nextCropping = !modeCrop;
      const thumbnail = thumbnails.find((item) => item.id === selectedThumbnailId);
      if (!thumbnail) {
        toast.error('Thumbnail was not found', { duration: 3000 });
        return modeCrop;
      }

      setMode(getModeByValue(nextCropping ? 'cropping' : 'editing'));

      if (nextCropping) {
        setCroppedImage('');
      } else {
        setCroppedImage(thumbnail?.croppedImage || '');
      }

      return nextCropping;
    });
  };

  const currentIndex =
    thumbnails.findIndex((thumbnail) => thumbnail.id === selectedThumbnailId) + 1 || thumbnails.length + 1;

  const handleCaptureScanning = async (imageSrc: string) => {
    try {
      const id = uuidv4();
      const thumbnailImage = await resizeImage(imageSrc, 300, 300);
      setThumbnails((thumbnailsList) => [
        ...thumbnailsList,
        { id, image: imageSrc, croppedImage: '', thumbnailImage, order: getNewOrder() },
      ]);
      setSelectedThumbnailId(id);
      setOriginalImage(imageSrc);
      setCropMode(true);
      setMode(getModeByValue('cropping'));
    } catch (error) {
      console.error(error);
    }
  };

  const handleRetake = () => {
    if (typeof selectedThumbnailId === 'string') {
      clearAllTempThumbnails();
      return resetToScan();
    }
    saveBufferImage();
    setMode(getModeByValue('retaking'));
  };

  const turnOffRetakeMode = () => {
    applyBufferImage();
    setMode(getModeByValue('editing'));
  };

  const handleSaveChanges = async () => {
    if (!selectedThumbnailId) return;

    let pieceDoc = null;

    if (!sessionPieceDoc) {
      const operationId = uuidv4();
      setPiecePageInitOperations((operationIds) => [...operationIds, operationId]);
      pieceDoc = await createPieceDocument({ pieceId: currentPiece.id, skipInvalidation: true }).unwrap();
      setSessionPieceDoc(pieceDoc);
      setPiecePageInitOperations((operationIds) => operationIds.filter((id) => id !== operationId));
    } else {
      pieceDoc = sessionPieceDoc;
    }

    const thumbnail = thumbnails.find((item) => item.id === selectedThumbnailId);

    if (!thumbnail) {
      toast.error('Thumbnail was not found', { duration: 3000 });
      return;
    }

    const originalImageHasChanged = thumbnail?.image !== originalImage;
    const croppedImageHasChanged = thumbnail?.croppedImage !== croppedImage;

    // if images are the same on editing thumbnail
    if (!originalImageHasChanged && !croppedImageHasChanged && typeof thumbnail.id === 'number') {
      resetToScan();
      return;
    }

    let correctOriginalImage: string = '',
      correctCroppedImage: string = '';

    if (typeof thumbnail.id === 'number') {
      correctOriginalImage = originalImageHasChanged ? originalImage : '';
      correctCroppedImage = croppedImageHasChanged ? croppedImage : '';
    } else {
      correctOriginalImage = originalImage;
      correctCroppedImage = croppedImage;
    }

    const images = {
      originalImage: correctOriginalImage,
      croppedImage: correctCroppedImage,
    };

    const operationId = runPiecePageInitiation(
      {
        thumbnail,
        pieceDoc: pieceDoc,
        pieceId: currentPiece.id,
      },
      images,
      onPiecePageInitOperationComplete,
      onPiecePageInitOperationError,
    );

    setPiecePageInitOperations([...piecePageInitOperations, operationId]);

    resetToScan();
  };

  const onThumbnailClick = (thumbnailId: IThumbnailId) => {
    if (previewLoading) return;

    const previousThumbnail = thumbnails.find((thumbnail) => thumbnail.id === selectedThumbnailId);
    if (previousThumbnail) {
      const originalImageHasChanged = previousThumbnail.image !== originalImage;
      const croppedImageHasChanged = previousThumbnail.croppedImage !== croppedImage;

      if (
        (typeof selectedThumbnailId === 'string' ||
          (typeof selectedThumbnailId === 'number' && (originalImageHasChanged || croppedImageHasChanged))) &&
        !discardThumbnailDialog
      ) {
        setDiscardThumbnailDialog(thumbnailId);
        return;
      }
    }

    selectThumnbail(thumbnailId);
  };

  const handleRemove = async () => {
    if (typeof selectedThumbnailId === 'string') {
      clearAllTempThumbnails();
      return resetToScan();
    }
    if (!selectedThumbnailId) {
      toast.error('Firstly choose page to delete', { duration: 3000 });
      return;
    }

    try {
      const thumbnail = thumbnails.find((item) => item.id === selectedThumbnailId);
      if (!thumbnail) {
        toast.error('Thumbnail was not found', { duration: 3000 });
        return;
      }

      const id = thumbnail?.id as number;

      await deletePiecePage({ pieceId: currentPiece.id, piecePageId: id }).unwrap();
      deleteThumbnail(id);
      resetToScan();
      handleDeleteClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCrop = async () => {
    const cropResult = await cropperRef.current?.done();
    if (!cropResult) return;
    const { img } = cropResult;
    setCroppedImage(img);
    turnOffCropMode();
    setMode(getModeByValue('editing'));
  };

  const capture = async (imageSrc: string) => {
    if (imageSrc) {
      try {
        if (mode.value === 'scanning') {
          handleCaptureScanning(imageSrc);
        }
        if (mode.value === 'retaking') {
          handleCaptureRetaking(imageSrc);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <DialogDarkFullscreen open onClose={onClose}>
      <CaptureHeader
        title={`${mode.title} Page ${currentIndex}`}
        onClose={onClose}
        actions={
          <>
            {!!piecePageInitOperations.length && (
              <Box sx={{ position: 'relative', width: '44px', height: '44px', p: '2px', mr: '16px' }}>
                <CloudUploadRounded
                  sx={{
                    position: 'absolute',
                    width: '20px',
                    height: '20px',
                    top: '50%',
                    left: '50%',
                    mt: '-10px',
                    ml: '-10px',
                    color: 'text.secondary',
                  }}
                />
                <CircularProgress aria-label="Loading..." />
              </Box>
            )}
            {mode.value === 'editing' && (
              <DoubleButton
                titleFirstButton="Cancel"
                titleSecondButton="Save Changes"
                onClickFirstButton={handleCancelEditing}
                onClickSecondButton={handleSaveChanges}
              />
            )}
            {mode.value === 'cropping' && (
              <DoubleButton
                titleFirstButton="Cancel"
                titleSecondButton="Confirm"
                onClickFirstButton={toggleCropMode}
                onClickSecondButton={handleCrop}
              />
            )}
            {mode.value === 'scanning' && Boolean(thumbnails.length) && (
              <SingleButton isMobile={isMobile} onClick={handleFinishScanning} title="Finish Scanning" />
            )}
            {mode.value === 'retaking' && (
              <SingleButton isMobile={isMobile} onClick={turnOffRetakeMode} title="Cancel" />
            )}
          </>
        }
        hideActions={mode.value === 'scanning' && !thumbnails.length}
      />

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: orientation === 'landscape' ? 'row' : 'column',
          overflow: 'hidden',
        }}
      >
        <CaptureThumbnails
          thumbnails={thumbnails}
          pieceId={currentPiece.id}
          pieceDocumentId={sessionPieceDoc?.id || currentPiece.pieceDoc?.id || null}
          onChangeThumbnails={setThumbnails}
          selectedThumbnailId={selectedThumbnailId}
          onClickThumbnail={onThumbnailClick}
        />

        <CameraCapture capture={capture} disabled={!['scanning', 'retaking'].includes(mode.value)} />

        {['editing', 'cropping'].includes(mode.value) &&
          (previewLoading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress aria-label="Loading..." />
            </Box>
          ) : (
            <ImageEditor
              imageBase64={croppedImage || originalImage}
              actions={
                <CaptureActions
                  handleRemove={typeof selectedThumbnailId === 'string' ? handleRemove : handleDeleteOpen}
                  handleRetake={handleRetake}
                  handleCrop={toggleCropMode}
                  handleRotate={handleRotateImage}
                  direction={isMobile ? 'up' : 'left'}
                />
              }
              croppingActive={cropMode}
              cropperRef={cropperRef}
            />
          ))}
      </Box>

      <DialogConfirmation
        open={deleteOpen}
        handleClose={handleDeleteClose}
        title="Delete Piece Page"
        content="Are you sure you want permanently delete this piece page?"
        acceptButtonHandleClick={handleRemove}
        cancelButtonHandleClick={handleDeleteClose}
        cancelButtonProps={{ sx: { border: 'none', color: 'white' }, variant: 'text' }}
        acceptButtonTitle="Delete"
        cancelButtonTitle="Close"
        type="error"
        loading={isDeleteLoading}
      />

      <DialogConfirmation
        open={!!discardThumbnailDialog}
        handleClose={handleCloseDiscardThumbnailDialog}
        title="Navigate away and discard page?"
        content={
          typeof selectedThumbnailId === 'string'
            ? 'This page won’t be saved if you navigate away now.'
            : 'Your changes won’t be saved if you navigate away now.'
        }
        acceptButtonHandleClick={handleConfirmDiscardThumbnailDialog}
        cancelButtonHandleClick={handleCloseDiscardThumbnailDialog}
        acceptButtonTitle="Discard Page"
        cancelButtonTitle="Continue Editing"
      />
    </DialogDarkFullscreen>
  );
};
